/** Certificate / document expiry badge tiers */
export function getCertStatus(dateStr) {
  if (!dateStr) {
    return {
      tier: 'missing',
      label: 'N/A',
      className: 'bg-white text-logi-text0 ring-logi-grid',
      blink: false,
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  const days = Math.ceil((target - today) / (1000 * 60 * 60 * 24));

  if (days < 7) {
    return {
      tier: 'critical',
      label: days < 0 ? 'Expired' : `${days}d`,
      className: 'bg-red-500/15 text-red-700 ring-red-500/30',
      blink: true,
    };
  }

  if (days < 30) {
    return {
      tier: 'warning',
      label: `${days}d`,
      className: 'bg-amber-500/15 text-amber-700 ring-amber-500/30',
      blink: false,
    };
  }

  return {
    tier: 'valid',
    label: 'Valid',
    className: 'bg-emerald-500/15 text-emerald-700 ring-emerald-500/30',
    blink: false,
  };
}

export function formatCertDate(dateStr) {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  }).format(new Date(`${dateStr}T00:00:00`));
}

export const CERT_FIELDS = [
  { key: 'heavyVehicleLicense', label: 'HV Lic', shortLabel: 'HV' },
  { key: 'adrCertificate', label: 'ADR', shortLabel: 'ADR' },
  { key: 'h2sTraining', label: 'H2S', shortLabel: 'H2S' },
  { key: 'defensiveDriving', label: 'Def. Drv', shortLabel: 'DEF' },
  { key: 'firstAid', label: 'First Aid', shortLabel: 'FA' },
  { key: 'fireFighting', label: 'Fire Fgt', shortLabel: 'FF' },
  { key: 'adnocApproval', label: 'ADNOC', shortLabel: 'ADNOC' },
  { key: 'enocApproval', label: 'ENOC', shortLabel: 'ENOC' },
  { key: 'portAccess', label: 'Port', shortLabel: 'PORT' },
];

/** Modal checkbox grid — blueprint certifications */
export const MODAL_CERT_OPTIONS = [
  { key: 'heavyVehicleLicense', label: 'Heavy Vehicle License', shortLabel: 'HV' },
  { key: 'adrCertificate', label: 'Dangerous Goods (ADR)', shortLabel: 'ADR' },
  { key: 'h2sTraining', label: 'H2S Training', shortLabel: 'H2S' },
  { key: 'defensiveDriving', label: 'Defensive Driving', shortLabel: 'DEF' },
  { key: 'firstAid', label: 'First Aid', shortLabel: 'FA' },
  { key: 'fireFighting', label: 'Fire Fighting', shortLabel: 'FF' },
  { key: 'adnocApproval', label: 'ADNOC Approval', shortLabel: 'ADNOC' },
  { key: 'enocApproval', label: 'ENOC Approval', shortLabel: 'ENOC' },
];

export const EMPTY_IDENTITY_VAULT = {
  emiratesIdFront: null,
  emiratesIdBack: null,
  passportCopy: null,
  visaCopy: null,
  hvLicenseCopy: null,
};

export const IDENTITY_VAULT_FIELDS = [
  { key: 'emiratesIdFront', label: 'Emirates ID — Front Copy' },
  { key: 'emiratesIdBack', label: 'Emirates ID — Back Copy' },
  { key: 'passportCopy', label: 'Passport Copy' },
  { key: 'visaCopy', label: 'Visa Copy' },
  { key: 'hvLicenseCopy', label: 'Heavy Vehicle Driving License Copy' },
];

export const FOOD_ALLOWANCE_TYPES = ['Fixed Monthly', 'Per Duty Day'];

export const OVERTIME_RULES = ['Standard 1.5x', 'Flat Rate 25 AED/hr', 'None'];

export const SPECIAL_ALLOWANCE_TYPES = [
  'ADR/Hazmat Bonus',
  'Night Shift Premium',
  'Border Crossing Allowance',
  'Port Access Stipend',
  'Desert Allowance',
];

export const DEFAULT_CONTRACT_DETAILS = {
  basicSalary: 3500,
  foodAllowanceType: 'Fixed Monthly',
  foodAllowanceAmount: 500,
  overtimeRule: 'Standard 1.5x',
  specialAllowances: [],
};

export function normalizeContractDetails(raw) {
  return {
    basicSalary: Number(raw?.basicSalary) || DEFAULT_CONTRACT_DETAILS.basicSalary,
    foodAllowanceType: FOOD_ALLOWANCE_TYPES.includes(raw?.foodAllowanceType)
      ? raw.foodAllowanceType
      : DEFAULT_CONTRACT_DETAILS.foodAllowanceType,
    foodAllowanceAmount:
      Number(raw?.foodAllowanceAmount) || DEFAULT_CONTRACT_DETAILS.foodAllowanceAmount,
    overtimeRule: OVERTIME_RULES.includes(raw?.overtimeRule)
      ? raw.overtimeRule
      : DEFAULT_CONTRACT_DETAILS.overtimeRule,
    specialAllowances: Array.isArray(raw?.specialAllowances)
      ? raw.specialAllowances
          .map((item) => ({
            type: String(item?.type || '').trim(),
            amount: Number(item?.amount) || 0,
          }))
          .filter((item) => item.type)
      : [],
  };
}

export const EMPTY_DRIVER_MODAL_FORM = {
  name: '',
  nationality: '',
  mobile: '',
  emiratesId: '',
  passport: '',
  visa: '',
  drivingLicenseNumber: '',
  selectedCerts: [],
  profilePhoto: null,
  identityVault: { ...EMPTY_IDENTITY_VAULT },
  certAttachments: {},
  contractDetails: { ...DEFAULT_CONTRACT_DETAILS, specialAllowances: [] },
};

export function generateMockDriverDocLink(driverId, docType, fileName) {
  const slug = (driverId || 'draft').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  return `https://docs.ogts-logicore.ae/drivers/${slug}/${docType}/${encodeURIComponent(fileName)}`;
}

export function simulateDriverPhoto(file, driverId) {
  return {
    fileName: file.name,
    previewUrl: URL.createObjectURL(file),
    mockUrl: generateMockDriverDocLink(driverId || 'draft', 'biometric-photo', file.name),
    uploadedAt: new Date().toISOString().slice(0, 10),
  };
}

export function simulateIdentityDoc(file, driverId, docKey) {
  return {
    fileName: file.name,
    mockUrl: generateMockDriverDocLink(driverId || 'draft', docKey, file.name),
    uploadedAt: new Date().toISOString().slice(0, 10),
  };
}

export function simulateCertDoc(file, driverId, certKey) {
  return {
    fileName: file.name,
    mockUrl: generateMockDriverDocLink(driverId || 'draft', `cert-${certKey}`, file.name),
    uploadedAt: new Date().toISOString().slice(0, 10),
  };
}

function mockDoc(driverId, docKey, fileName) {
  return {
    fileName,
    mockUrl: generateMockDriverDocLink(driverId, docKey, fileName),
    uploadedAt: '2026-01-15',
  };
}

function mockCertDoc(driverId, certKey, fileName) {
  return {
    fileName,
    mockUrl: generateMockDriverDocLink(driverId, `cert-${certKey}`, fileName),
    uploadedAt: '2026-01-15',
  };
}

const MOCK_DRIVER_PHOTOS = [
  'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&h=200&fit=crop&face',
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&face',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&face',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&face',
  'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200&h=200&fit=crop&face',
  'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&h=200&fit=crop&face',
  'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop&face',
  'https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=200&h=200&fit=crop&face',
  'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&face',
  'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200&h=200&fit=crop&face',
];

function mockProfilePhoto(driverId, fileName, photoIndex) {
  return {
    fileName,
    previewUrl: MOCK_DRIVER_PHOTOS[photoIndex % MOCK_DRIVER_PHOTOS.length],
    mockUrl: generateMockDriverDocLink(driverId, 'biometric-photo', fileName),
    uploadedAt: '2026-01-15',
  };
}

function buildDriver({
  id,
  name,
  nationality,
  mobile,
  emiratesId,
  passport,
  visa,
  licenseNo,
  licenseExpiry,
  photoIndex,
  photoFile,
  vaultDocs = {},
  certDocs = {},
  certs = {},
  contractDetails,
}) {
  return {
    id,
    name,
    nationality,
    mobile,
    emiratesId,
    passport,
    visa,
    drivingLicenseNumber: licenseNo,
    heavyVehicleLicense: `${licenseNo} · Exp ${licenseExpiry}`,
    profilePhoto: mockProfilePhoto(id, photoFile, photoIndex),
    identityVault: {
      emiratesIdFront: vaultDocs.emiratesIdFront ?? mockDoc(id, 'emiratesIdFront', `eid_front_${id.toLowerCase()}.pdf`),
      emiratesIdBack: vaultDocs.emiratesIdBack ?? mockDoc(id, 'emiratesIdBack', `eid_back_${id.toLowerCase()}.pdf`),
      passportCopy: vaultDocs.passportCopy ?? mockDoc(id, 'passportCopy', `passport_${id.toLowerCase()}.pdf`),
      visaCopy: vaultDocs.visaCopy ?? (nationality === 'UAE' ? null : mockDoc(id, 'visaCopy', `visa_${id.toLowerCase()}.pdf`)),
      hvLicenseCopy: vaultDocs.hvLicenseCopy ?? mockDoc(id, 'hvLicenseCopy', `hv_license_${id.toLowerCase()}.pdf`),
    },
    certAttachments: certDocs,
    certifications: {
      heavyVehicleLicense: licenseExpiry,
      ...certs,
    },
    contractDetails: normalizeContractDetails(contractDetails),
  };
}

export const DRIVER_PROFILES = [
  buildDriver({
    id: 'DRV-001',
    name: 'Ahmed Al Mansoori',
    nationality: 'UAE',
    mobile: '+971 50 123 4567',
    emiratesId: '784-1988-1234567-1',
    passport: 'P-UAE-88421',
    visa: 'Citizen — N/A',
    licenseNo: 'HL-AD-992834',
    licenseExpiry: '2027-04-12',
    photoIndex: 0,
    photoFile: 'ahmed_mansoori_passport_photo.jpg',
    certDocs: {
      adrCertificate: mockCertDoc('DRV-001', 'adrCertificate', 'adr_cert_ahmed.pdf'),
      h2sTraining: mockCertDoc('DRV-001', 'h2sTraining', 'h2s_training_ahmed.pdf'),
      firstAid: mockCertDoc('DRV-001', 'firstAid', 'first_aid_ahmed.pdf'),
      adnocApproval: mockCertDoc('DRV-001', 'adnocApproval', 'adnoc_clearance_ahmed.pdf'),
    },
    certs: {
      adrCertificate: '2026-09-15',
      h2sTraining: '2026-06-17',
      defensiveDriving: '2027-01-20',
      firstAid: '2026-08-01',
      fireFighting: '2026-07-10',
      adnocApproval: '2026-06-14',
      enocApproval: '2026-11-30',
      portAccess: '2026-07-05',
    },
    contractDetails: {
      basicSalary: 4500,
      foodAllowanceType: 'Fixed Monthly',
      foodAllowanceAmount: 600,
      overtimeRule: 'Standard 1.5x',
      specialAllowances: [{ type: 'ADR/Hazmat Bonus', amount: 300 }],
    },
  }),
  buildDriver({
    id: 'DRV-002',
    name: 'Rajesh Kumar',
    nationality: 'Indian',
    mobile: '+971 52 987 6543',
    emiratesId: '784-1992-7654321-3',
    passport: 'Z-IND-442910 · Mumbai RPO',
    visa: 'Employment · Exp 2027-02-28',
    licenseNo: 'HL-DXB-771023',
    licenseExpiry: '2026-10-15',
    photoIndex: 1,
    photoFile: 'rajesh_kumar_passport_photo.jpg',
    certDocs: {
      adrCertificate: mockCertDoc('DRV-002', 'adrCertificate', 'adr_cert_rajesh.pdf'),
      h2sTraining: mockCertDoc('DRV-002', 'h2sTraining', 'h2s_rajesh.pdf'),
      adnocApproval: mockCertDoc('DRV-002', 'adnocApproval', 'adnoc_rajesh.pdf'),
    },
    certs: {
      adrCertificate: '2026-12-01',
      h2sTraining: '2026-10-22',
      defensiveDriving: '2026-07-28',
      firstAid: '2027-03-15',
      fireFighting: '2026-09-01',
      adnocApproval: '2026-08-20',
      enocApproval: '2026-06-25',
      portAccess: '2026-09-14',
    },
    contractDetails: {
      basicSalary: 3800,
      foodAllowanceType: 'Per Duty Day',
      foodAllowanceAmount: 20,
      overtimeRule: 'Flat Rate 25 AED/hr',
      specialAllowances: [{ type: 'Night Shift Premium', amount: 250 }],
    },
  }),
  buildDriver({
    id: 'DRV-003',
    name: 'Mohammed Hassan',
    nationality: 'Pakistani',
    mobile: '+971 55 456 7890',
    emiratesId: '784-1985-9876543-2',
    passport: 'PK-PA-339012 · Lahore',
    visa: 'Employment · Exp 2026-09-30',
    licenseNo: 'HL-AUH-556789',
    licenseExpiry: '2026-07-30',
    photoIndex: 2,
    photoFile: 'mohammed_hassan_passport_photo.jpg',
    certDocs: {
      h2sTraining: mockCertDoc('DRV-003', 'h2sTraining', 'h2s_hassan.pdf'),
      firstAid: mockCertDoc('DRV-003', 'firstAid', 'first_aid_hassan.pdf'),
      adrCertificate: mockCertDoc('DRV-003', 'adrCertificate', 'adr_hassan.pdf'),
    },
    certs: {
      adrCertificate: '2026-06-20',
      h2sTraining: '2026-08-15',
      defensiveDriving: '2026-11-01',
      firstAid: '2026-06-18',
      fireFighting: '2027-01-10',
      adnocApproval: '2026-10-01',
      enocApproval: '2026-07-15',
      portAccess: '2026-12-20',
    },
    contractDetails: {
      basicSalary: 4200,
      foodAllowanceType: 'Fixed Monthly',
      foodAllowanceAmount: 550,
      overtimeRule: 'Standard 1.5x',
      specialAllowances: [
        { type: 'ADR/Hazmat Bonus', amount: 300 },
        { type: 'Port Access Stipend', amount: 150 },
      ],
    },
  }),
  buildDriver({
    id: 'DRV-004',
    name: 'Yusuf Ibrahim',
    nationality: 'Bangladeshi',
    mobile: '+971 56 321 0987',
    emiratesId: '784-1990-4567890-4',
    passport: 'BD-BD-778234 · Dhaka',
    visa: 'Employment · Exp 2027-06-15',
    licenseNo: 'HL-SHJ-334521',
    licenseExpiry: '2027-05-20',
    photoIndex: 3,
    photoFile: 'yusuf_ibrahim_passport_photo.jpg',
    certDocs: {
      h2sTraining: mockCertDoc('DRV-004', 'h2sTraining', 'h2s_yusuf.pdf'),
      adnocApproval: mockCertDoc('DRV-004', 'adnocApproval', 'adnoc_yusuf.pdf'),
      enocApproval: mockCertDoc('DRV-004', 'enocApproval', 'enoc_yusuf.pdf'),
    },
    certs: {
      adrCertificate: '2027-02-10',
      h2sTraining: '2027-01-05',
      defensiveDriving: '2026-09-30',
      firstAid: '2026-10-12',
      fireFighting: '2026-11-25',
      adnocApproval: '2026-08-01',
      enocApproval: '2026-12-15',
      portAccess: '2027-03-01',
    },
    contractDetails: {
      basicSalary: 3500,
      foodAllowanceType: 'Per Duty Day',
      foodAllowanceAmount: 18,
      overtimeRule: 'None',
      specialAllowances: [],
    },
  }),
  buildDriver({
    id: 'DRV-005',
    name: 'Imran Ali Khan',
    nationality: 'Pakistani',
    mobile: '+971 54 210 8834',
    emiratesId: '784-1991-3344556-7',
    passport: 'PK-IS-881204 · Islamabad',
    visa: 'Employment · Exp 2027-08-10',
    licenseNo: 'HL-AJM-445901',
    licenseExpiry: '2027-02-18',
    photoIndex: 4,
    photoFile: 'imran_ali_passport_photo.jpg',
    certDocs: {
      h2sTraining: mockCertDoc('DRV-005', 'h2sTraining', 'h2s_imran.pdf'),
      adrCertificate: mockCertDoc('DRV-005', 'adrCertificate', 'adr_imran.pdf'),
      defensiveDriving: mockCertDoc('DRV-005', 'defensiveDriving', 'defensive_imran.pdf'),
    },
    certs: {
      adrCertificate: '2027-01-12',
      h2sTraining: '2026-11-08',
      defensiveDriving: '2027-03-20',
      firstAid: '2026-12-05',
      fireFighting: '2027-02-01',
      adnocApproval: '2027-04-15',
      enocApproval: '2026-10-30',
      portAccess: '2027-06-01',
    },
    contractDetails: {
      basicSalary: 3600,
      foodAllowanceType: 'Fixed Monthly',
      foodAllowanceAmount: 500,
      overtimeRule: 'Flat Rate 25 AED/hr',
      specialAllowances: [{ type: 'Desert Allowance', amount: 200 }],
    },
  }),
  buildDriver({
    id: 'DRV-006',
    name: 'Vijay Nair',
    nationality: 'Indian',
    mobile: '+971 58 774 2201',
    emiratesId: '784-1989-1122334-5',
    passport: 'K-IND-667812 · Kochi',
    visa: 'Employment · Exp 2027-11-22',
    licenseNo: 'HL-FUJ-889012',
    licenseExpiry: '2027-08-30',
    photoIndex: 5,
    photoFile: 'vijay_nair_passport_photo.jpg',
    certDocs: {
      h2sTraining: mockCertDoc('DRV-006', 'h2sTraining', 'h2s_vijay.pdf'),
      adrCertificate: mockCertDoc('DRV-006', 'adrCertificate', 'adr_vijay.pdf'),
      enocApproval: mockCertDoc('DRV-006', 'enocApproval', 'enoc_vijay.pdf'),
    },
    certs: {
      adrCertificate: '2027-05-18',
      h2sTraining: '2027-02-14',
      defensiveDriving: '2026-08-22',
      firstAid: '2027-01-30',
      fireFighting: '2027-04-08',
      adnocApproval: '2027-06-12',
      enocApproval: '2026-09-25',
      portAccess: '2027-07-01',
    },
    contractDetails: {
      basicSalary: 4100,
      foodAllowanceType: 'Fixed Monthly',
      foodAllowanceAmount: 600,
      overtimeRule: 'Standard 1.5x',
      specialAllowances: [{ type: 'ADR/Hazmat Bonus', amount: 300 }],
    },
  }),
  buildDriver({
    id: 'DRV-007',
    name: 'Rafiq Ahmed',
    nationality: 'Bangladeshi',
    mobile: '+971 52 661 9044',
    emiratesId: '784-1993-7788990-1',
    passport: 'BD-CH-445901 · Chittagong',
    visa: 'Employment · Exp 2026-12-31',
    licenseNo: 'HL-RAK-667234',
    licenseExpiry: '2026-09-14',
    photoIndex: 6,
    photoFile: 'rafiq_ahmed_passport_photo.jpg',
    certDocs: {
      h2sTraining: mockCertDoc('DRV-007', 'h2sTraining', 'h2s_rafiq.pdf'),
      firstAid: mockCertDoc('DRV-007', 'firstAid', 'first_aid_rafiq.pdf'),
    },
    certs: {
      adrCertificate: '2026-10-05',
      h2sTraining: '2026-07-02',
      defensiveDriving: '2026-12-18',
      firstAid: '2027-02-22',
      fireFighting: '2026-08-30',
      adnocApproval: '2026-11-11',
      enocApproval: '2027-01-08',
      portAccess: '2026-09-20',
    },
    contractDetails: {
      basicSalary: 3400,
      foodAllowanceType: 'Per Duty Day',
      foodAllowanceAmount: 20,
      overtimeRule: 'Flat Rate 25 AED/hr',
      specialAllowances: [],
    },
  }),
  buildDriver({
    id: 'DRV-008',
    name: 'Arjun Singh',
    nationality: 'Indian',
    mobile: '+971 50 339 7712',
    emiratesId: '784-1994-5566778-9',
    passport: 'P-IND-991023 · Punjab',
    visa: 'Employment · Exp 2028-01-05',
    licenseNo: 'HL-DXB-992341',
    licenseExpiry: '2027-11-05',
    photoIndex: 7,
    photoFile: 'arjun_singh_passport_photo.jpg',
    certDocs: {
      h2sTraining: mockCertDoc('DRV-008', 'h2sTraining', 'h2s_arjun.pdf'),
      adrCertificate: mockCertDoc('DRV-008', 'adrCertificate', 'adr_arjun.pdf'),
      adnocApproval: mockCertDoc('DRV-008', 'adnocApproval', 'adnoc_arjun.pdf'),
      portAccess: mockCertDoc('DRV-008', 'portAccess', 'port_access_arjun.pdf'),
    },
    certs: {
      adrCertificate: '2027-09-01',
      h2sTraining: '2027-06-20',
      defensiveDriving: '2027-02-28',
      firstAid: '2027-04-10',
      fireFighting: '2027-03-15',
      adnocApproval: '2027-08-22',
      enocApproval: '2027-05-30',
      portAccess: '2027-10-01',
    },
    contractDetails: {
      basicSalary: 3900,
      foodAllowanceType: 'Fixed Monthly',
      foodAllowanceAmount: 550,
      overtimeRule: 'Standard 1.5x',
      specialAllowances: [{ type: 'Border Crossing Allowance', amount: 400 }],
    },
  }),
  buildDriver({
    id: 'DRV-009',
    name: 'Kamran Siddiqui',
    nationality: 'Pakistani',
    mobile: '+971 55 882 4410',
    emiratesId: '784-1987-2233445-6',
    passport: 'PK-KR-112890 · Karachi',
    visa: 'Employment · Exp 2027-04-18',
    licenseNo: 'HL-AUH-778901',
    licenseExpiry: '2026-12-01',
    photoIndex: 8,
    photoFile: 'kamran_siddiqui_passport_photo.jpg',
    certDocs: {
      h2sTraining: mockCertDoc('DRV-009', 'h2sTraining', 'h2s_kamran.pdf'),
      adrCertificate: mockCertDoc('DRV-009', 'adrCertificate', 'adr_kamran.pdf'),
      fireFighting: mockCertDoc('DRV-009', 'fireFighting', 'fire_kamran.pdf'),
    },
    certs: {
      adrCertificate: '2027-01-25',
      h2sTraining: '2026-09-08',
      defensiveDriving: '2026-10-15',
      firstAid: '2026-11-20',
      fireFighting: '2027-02-05',
      adnocApproval: '2026-12-28',
      enocApproval: '2027-03-10',
      portAccess: '2027-01-15',
    },
    contractDetails: {
      basicSalary: 3700,
      foodAllowanceType: 'Per Duty Day',
      foodAllowanceAmount: 22,
      overtimeRule: 'Standard 1.5x',
      specialAllowances: [{ type: 'ADR/Hazmat Bonus', amount: 300 }],
    },
  }),
  buildDriver({
    id: 'DRV-010',
    name: 'Hasan Mahmud',
    nationality: 'Bangladeshi',
    mobile: '+971 56 119 5533',
    emiratesId: '784-1996-9900112-3',
    passport: 'BD-SY-334567 · Sylhet',
    visa: 'Employment · Exp 2027-09-01',
    licenseNo: 'HL-UAQ-556123',
    licenseExpiry: '2027-07-14',
    photoIndex: 9,
    photoFile: 'hasan_mahmud_passport_photo.jpg',
    certDocs: {
      h2sTraining: mockCertDoc('DRV-010', 'h2sTraining', 'h2s_hasan.pdf'),
      enocApproval: mockCertDoc('DRV-010', 'enocApproval', 'enoc_hasan.pdf'),
      defensiveDriving: mockCertDoc('DRV-010', 'defensiveDriving', 'defensive_hasan.pdf'),
    },
    certs: {
      adrCertificate: '2027-04-02',
      h2sTraining: '2027-01-18',
      defensiveDriving: '2027-05-08',
      firstAid: '2026-12-12',
      fireFighting: '2027-06-01',
      adnocApproval: '2027-07-20',
      enocApproval: '2026-08-28',
      portAccess: '2027-08-15',
    },
    contractDetails: {
      basicSalary: 3300,
      foodAllowanceType: 'Fixed Monthly',
      foodAllowanceAmount: 450,
      overtimeRule: 'None',
      specialAllowances: [],
    },
  }),
];

/** Authorized terminal geofences — 0.05° bounding radius from anchor coordinates */
export const GEOFENCE_RADIUS_DEG = 0.05;

export const TERMINAL_GPS_ANCHORS = [
  {
    id: 'jebel-ali',
    name: 'Jebel Ali Terminal',
    lat: 24.98,
    lng: 55.08,
    displayCoords: '24.980°N, 55.080°E',
  },
  {
    id: 'ruwais',
    name: 'Ruwais Depot',
    lat: 24.106,
    lng: 52.73,
    displayCoords: '24.106°N, 52.730°E',
  },
  {
    id: 'dic',
    name: 'DIC Depot',
    lat: 25.128,
    lng: 55.382,
    displayCoords: '25.128°N, 55.382°E',
  },
];

export const AUTHORIZED_GEOFENCES = TERMINAL_GPS_ANCHORS.map((t) => ({
  id: t.id,
  name: t.name,
  latRange: [t.lat - GEOFENCE_RADIUS_DEG, t.lat + GEOFENCE_RADIUS_DEG],
  lngRange: [t.lng - GEOFENCE_RADIUS_DEG, t.lng + GEOFENCE_RADIUS_DEG],
  displayCoords: t.displayCoords,
}));

export const MOBILE_API_SUCCESS_LABEL = 'Verified via Driver Mobile App (GPS Active)';
export const MOBILE_API_FAILED_LABEL = 'Geofence Breach: Unauthorized Location';
export const MOBILE_APP_VERSION = '2.4.1 (142)';

/** Simulator dropdown — explicit mobile GPS feed control */
export const GPS_FEED_SOURCES = [
  {
    id: 'jebel-ali',
    label: 'Jebel Ali Terminal (Valid)',
    preset: 'terminal',
    terminalId: 'jebel-ali',
  },
  {
    id: 'ruwais',
    label: 'Ruwais Depot (Valid)',
    preset: 'terminal',
    terminalId: 'ruwais',
  },
  {
    id: 'e11-highway',
    label: 'E11 Highway - Unknown Roadside (Invalid)',
    preset: 'invalid',
    lat: 25.052,
    lng: 55.312,
  },
  {
    id: 'custom',
    label: 'Custom Lat/Long Coordinates',
    preset: 'custom',
  },
];

/** @deprecated use GPS_FEED_SOURCES */
export const API_PUNCH_LOCATIONS = GPS_FEED_SOURCES.filter((s) => s.preset !== 'custom').map(
  (s) => ({
    id: s.id,
    label: s.label,
    authorized: s.preset === 'terminal',
    terminalName:
      s.preset === 'terminal'
        ? TERMINAL_GPS_ANCHORS.find((t) => t.id === s.terminalId)?.name
        : 'E11 Highway - Unknown Roadside',
    geofenceId: s.preset === 'terminal' ? s.terminalId : null,
  }),
);

export const SIMULATED_LOCATIONS = API_PUNCH_LOCATIONS;

export function findMatchedTerminal(lat, lng) {
  if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return (
    TERMINAL_GPS_ANCHORS.find(
      (t) =>
        Math.abs(lat - t.lat) <= GEOFENCE_RADIUS_DEG &&
        Math.abs(lng - t.lng) <= GEOFENCE_RADIUS_DEG,
    ) || null
  );
}

export function resolveGpsFeedInput(gpsFeedSourceId, customLat = '', customLng = '') {
  const source = GPS_FEED_SOURCES.find((s) => s.id === gpsFeedSourceId) || GPS_FEED_SOURCES[0];

  if (source.preset === 'terminal') {
    const terminal = TERMINAL_GPS_ANCHORS.find((t) => t.id === source.terminalId);
    return {
      lat: terminal.lat,
      lng: terminal.lng,
      authorized: true,
      locationLabel: terminal.name,
      matchedTerminalId: terminal.id,
      gpsFeedSourceId: source.id,
      gpsFeedSourceLabel: source.label,
    };
  }

  if (source.preset === 'invalid') {
    return {
      lat: source.lat,
      lng: source.lng,
      authorized: false,
      locationLabel: 'E11 Highway - Unknown Roadside',
      matchedTerminalId: null,
      gpsFeedSourceId: source.id,
      gpsFeedSourceLabel: source.label,
    };
  }

  const lat = parseFloat(String(customLat).trim());
  const lng = parseFloat(String(customLng).trim());

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return {
      lat: null,
      lng: null,
      authorized: false,
      locationLabel: 'Custom GPS — invalid coordinates',
      matchedTerminalId: null,
      gpsFeedSourceId: source.id,
      gpsFeedSourceLabel: source.label,
    };
  }

  const matched = findMatchedTerminal(lat, lng);
  return {
    lat,
    lng,
    authorized: Boolean(matched),
    locationLabel: matched
      ? matched.name
      : `Custom GPS (${lat.toFixed(4)}°, ${lng.toFixed(4)}°)`,
    matchedTerminalId: matched?.id || null,
    gpsFeedSourceId: source.id,
    gpsFeedSourceLabel: source.label,
  };
}

export function previewGpsFeed(gpsFeedSourceId, customLat, customLng) {
  return resolveGpsFeedInput(gpsFeedSourceId, customLat, customLng);
}

export function mockDeviceId(driverId) {
  const n = parseInt(driverId.replace('DRV-', ''), 10);
  const platform = n % 3 === 0 ? 'IOS' : 'AND';
  return `OGTS-${platform}-${String(10000 + n * 417).slice(-5)}`;
}

export function formatApiTimestamp(dateOrIso) {
  const d = typeof dateOrIso === 'string' ? new Date(dateOrIso) : dateOrIso;
  const time = d.toLocaleTimeString('en-AE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZone: 'Asia/Dubai',
  });
  return `${formatPunchDate(d)} · ${time} GST`;
}

export function getApiPunchLocation(locationId) {
  const gps = GPS_FEED_SOURCES.find((s) => s.id === locationId);
  if (!gps) return { id: locationId, authorized: false, terminalName: 'Unknown' };
  if (gps.preset === 'terminal') {
    const t = TERMINAL_GPS_ANCHORS.find((a) => a.id === gps.terminalId);
    return { id: gps.id, authorized: true, terminalName: t?.name };
  }
  return { id: gps.id, authorized: false, terminalName: 'E11 Highway - Unknown Roadside' };
}

export function getSimulatedLocation(locationId) {
  return getApiPunchLocation(locationId);
}

export function isAuthorizedSimLocation(locationId) {
  return resolveGpsFeedInput(locationId).authorized;
}

export function buildApiFeedEvent({
  driverId,
  driverName,
  punchType,
  gpsFeedSourceId,
  customLat = '',
  customLng = '',
  timestamp = new Date(),
  deviceId,
  appVersion,
}) {
  const gps = resolveGpsFeedInput(gpsFeedSourceId, customLat, customLng);
  const ts = timestamp instanceof Date ? timestamp.toISOString() : timestamp;

  return {
    id: `API-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    driverId,
    driverName,
    deviceId: deviceId || mockDeviceId(driverId),
    appVersion: appVersion || MOBILE_APP_VERSION,
    timestamp: ts,
    timestampDisplay: formatApiTimestamp(ts),
    punchType,
    location: gps.locationLabel,
    latitude: gps.lat,
    longitude: gps.lng,
    gpsFeedSource: gps.gpsFeedSourceId,
    gpsFeedSourceLabel: gps.gpsFeedSourceLabel,
    matchedTerminalId: gps.matchedTerminalId,
    geofenceStatus: gps.authorized ? 'success' : 'failed',
    statusLabel: gps.authorized ? MOBILE_API_SUCCESS_LABEL : MOBILE_API_FAILED_LABEL,
  };
}

function mockFeed({
  id,
  driverId,
  driverName,
  timestamp,
  punchType,
  location,
  geofenceStatus,
  deviceId,
  appVersion = MOBILE_APP_VERSION,
  latitude = null,
  longitude = null,
  gpsFeedSource = null,
}) {
  return {
    id,
    driverId,
    driverName,
    deviceId: deviceId || mockDeviceId(driverId),
    appVersion,
    timestamp,
    timestampDisplay: formatApiTimestamp(timestamp),
    punchType,
    location,
    latitude,
    longitude,
    gpsFeedSource,
    geofenceStatus,
    statusLabel:
      geofenceStatus === 'success' ? MOBILE_API_SUCCESS_LABEL : MOBILE_API_FAILED_LABEL,
  };
}

/** Incoming mobile app API punch feed — central integration listener */
export const ATTENDANCE_PUNCHES = [
  mockFeed({ id: 'API-001', driverId: 'DRV-001', driverName: 'Ahmed Al Mansoori', timestamp: '2026-06-12T05:42:18+04:00', punchType: 'PUNCH_IN', location: 'Jebel Ali Terminal', latitude: 24.98, longitude: 55.08, gpsFeedSource: 'jebel-ali', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-002', driverId: 'DRV-002', driverName: 'Rajesh Kumar', timestamp: '2026-06-12T02:48:05+04:00', punchType: 'PUNCH_IN', location: 'E11 Highway - Unknown Roadside', latitude: 25.052, longitude: 55.312, gpsFeedSource: 'e11-highway', geofenceStatus: 'failed' }),
  mockFeed({ id: 'API-003', driverId: 'DRV-002', driverName: 'Rajesh Kumar', timestamp: '2026-06-12T04:15:33+04:00', punchType: 'PUNCH_IN', location: 'Ruwais Depot', latitude: 24.106, longitude: 52.73, gpsFeedSource: 'ruwais', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-004', driverId: 'DRV-003', driverName: 'Mohammed Hassan', timestamp: '2026-06-12T03:30:12+04:00', punchType: 'PUNCH_IN', location: 'Ruwais Depot', latitude: 24.106, longitude: 52.73, gpsFeedSource: 'ruwais', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-005', driverId: 'DRV-003', driverName: 'Mohammed Hassan', timestamp: '2026-06-12T17:45:50+04:00', punchType: 'PUNCH_OUT', location: 'Jebel Ali Terminal', latitude: 24.98, longitude: 55.08, gpsFeedSource: 'jebel-ali', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-006', driverId: 'DRV-004', driverName: 'Yusuf Ibrahim', timestamp: '2026-06-12T06:38:22+04:00', punchType: 'PUNCH_IN', location: 'DIC Depot', latitude: 25.128, longitude: 55.382, gpsFeedSource: 'custom', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-007', driverId: 'DRV-004', driverName: 'Yusuf Ibrahim', timestamp: '2026-06-12T16:30:08+04:00', punchType: 'PUNCH_OUT', location: 'Jebel Ali Terminal', latitude: 24.98, longitude: 55.08, gpsFeedSource: 'jebel-ali', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-008', driverId: 'DRV-005', driverName: 'Imran Ali Khan', timestamp: '2026-06-12T05:55:41+04:00', punchType: 'PUNCH_IN', location: 'Jebel Ali Terminal', latitude: 24.98, longitude: 55.08, gpsFeedSource: 'jebel-ali', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-009', driverId: 'DRV-006', driverName: 'Vijay Nair', timestamp: '2026-06-12T07:12:19+04:00', punchType: 'PUNCH_IN', location: 'Custom GPS (25.2000°, 55.5000°)', latitude: 25.2, longitude: 55.5, gpsFeedSource: 'custom', geofenceStatus: 'failed' }),
  mockFeed({ id: 'API-010', driverId: 'DRV-007', driverName: 'Rafiq Ahmed', timestamp: '2026-06-12T05:10:02+04:00', punchType: 'PUNCH_IN', location: 'E11 Highway - Unknown Roadside', latitude: 25.052, longitude: 55.312, gpsFeedSource: 'e11-highway', geofenceStatus: 'failed' }),
  mockFeed({ id: 'API-011', driverId: 'DRV-007', driverName: 'Rafiq Ahmed', timestamp: '2026-06-12T05:28:44+04:00', punchType: 'PUNCH_IN', location: 'DIC Depot', latitude: 25.128, longitude: 55.382, gpsFeedSource: 'custom', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-012', driverId: 'DRV-007', driverName: 'Rafiq Ahmed', timestamp: '2026-06-12T16:10:30+04:00', punchType: 'PUNCH_OUT', location: 'DIC Depot', latitude: 25.128, longitude: 55.382, gpsFeedSource: 'custom', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-013', driverId: 'DRV-008', driverName: 'Arjun Singh', timestamp: '2026-06-12T05:05:17+04:00', punchType: 'PUNCH_IN', location: 'Jebel Ali Terminal', latitude: 24.98, longitude: 55.08, gpsFeedSource: 'jebel-ali', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-014', driverId: 'DRV-008', driverName: 'Arjun Singh', timestamp: '2026-06-12T17:50:55+04:00', punchType: 'PUNCH_OUT', location: 'Ruwais Depot', latitude: 24.106, longitude: 52.73, gpsFeedSource: 'ruwais', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-015', driverId: 'DRV-009', driverName: 'Kamran Siddiqui', timestamp: '2026-06-12T03:55:28+04:00', punchType: 'PUNCH_IN', location: 'E11 Highway - Unknown Roadside', latitude: 25.052, longitude: 55.312, gpsFeedSource: 'e11-highway', geofenceStatus: 'failed' }),
  mockFeed({ id: 'API-016', driverId: 'DRV-009', driverName: 'Kamran Siddiqui', timestamp: '2026-06-12T06:52:11+04:00', punchType: 'PUNCH_IN', location: 'Ruwais Refinery', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-017', driverId: 'DRV-010', driverName: 'Hasan Mahmud', timestamp: '2026-06-12T04:50:36+04:00', punchType: 'PUNCH_IN', location: 'Jebel Ali Terminal', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-018', driverId: 'DRV-010', driverName: 'Hasan Mahmud', timestamp: '2026-06-12T15:30:22+04:00', punchType: 'PUNCH_OUT', location: 'Jebel Ali Terminal', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-019', driverId: 'DRV-001', driverName: 'Ahmed Al Mansoori', timestamp: '2026-06-12T18:15:04+04:00', punchType: 'PUNCH_OUT', location: 'Jebel Ali Terminal', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-020', driverId: 'DRV-002', driverName: 'Rajesh Kumar', timestamp: '2026-06-11T05:58:09+04:00', punchType: 'PUNCH_IN', location: 'Jebel Ali Terminal', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-021', driverId: 'DRV-005', driverName: 'Imran Ali Khan', timestamp: '2026-06-11T04:22:51+04:00', punchType: 'PUNCH_IN', location: 'Unknown Roadside', geofenceStatus: 'failed' }),
  mockFeed({ id: 'API-022', driverId: 'DRV-006', driverName: 'Vijay Nair', timestamp: '2026-06-11T07:30:44+04:00', punchType: 'PUNCH_IN', location: 'DIC Depot', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-023', driverId: 'DRV-003', driverName: 'Mohammed Hassan', timestamp: '2026-06-10T04:02:18+04:00', punchType: 'PUNCH_IN', location: 'Ruwais Refinery', geofenceStatus: 'success' }),
  mockFeed({ id: 'API-024', driverId: 'DRV-008', driverName: 'Arjun Singh', timestamp: '2026-06-09T05:00:12+04:00', punchType: 'PUNCH_IN', location: 'Unknown Roadside', geofenceStatus: 'failed' }),
];

export const TIMESHEET_ALLOWANCES = [
  {
    driverId: 'DRV-001',
    driverName: 'Ahmed Al Mansoori',
    workingHours: 12.5,
    overtime: 2.5,
    nightAllowance: 150,
    tripAllowance: 200,
    waitingCharges: 75,
    borderCrossingAllowance: 0,
  },
  {
    driverId: 'DRV-002',
    driverName: 'Rajesh Kumar',
    workingHours: 14.0,
    overtime: 4.0,
    nightAllowance: 0,
    tripAllowance: 350,
    waitingCharges: 120,
    borderCrossingAllowance: 0,
  },
  {
    driverId: 'DRV-003',
    driverName: 'Mohammed Hassan',
    workingHours: 14.25,
    overtime: 3.25,
    nightAllowance: 200,
    tripAllowance: 400,
    waitingCharges: 0,
    borderCrossingAllowance: 250,
  },
  {
    driverId: 'DRV-004',
    driverName: 'Yusuf Ibrahim',
    workingHours: 10.33,
    overtime: 0,
    nightAllowance: 0,
    tripAllowance: 180,
    waitingCharges: 60,
    borderCrossingAllowance: 0,
  },
  {
    driverId: 'DRV-005',
    driverName: 'Imran Ali Khan',
    workingHours: 12.08,
    overtime: 2.0,
    nightAllowance: 100,
    tripAllowance: 280,
    waitingCharges: 45,
    borderCrossingAllowance: 0,
  },
  {
    driverId: 'DRV-006',
    driverName: 'Vijay Nair',
    workingHours: 13.5,
    overtime: 3.5,
    nightAllowance: 150,
    tripAllowance: 320,
    waitingCharges: 90,
    borderCrossingAllowance: 150,
  },
  {
    driverId: 'DRV-007',
    driverName: 'Rafiq Ahmed',
    workingHours: 11.75,
    overtime: 1.75,
    nightAllowance: 0,
    tripAllowance: 220,
    waitingCharges: 55,
    borderCrossingAllowance: 0,
  },
  {
    driverId: 'DRV-008',
    driverName: 'Arjun Singh',
    workingHours: 12.75,
    overtime: 2.75,
    nightAllowance: 120,
    tripAllowance: 290,
    waitingCharges: 40,
    borderCrossingAllowance: 0,
  },
  {
    driverId: 'DRV-009',
    driverName: 'Kamran Siddiqui',
    workingHours: 11.0,
    overtime: 1.0,
    nightAllowance: 80,
    tripAllowance: 260,
    waitingCharges: 70,
    borderCrossingAllowance: 200,
  },
  {
    driverId: 'DRV-010',
    driverName: 'Hasan Mahmud',
    workingHours: 10.67,
    overtime: 0.67,
    nightAllowance: 0,
    tripAllowance: 195,
    waitingCharges: 35,
    borderCrossingAllowance: 0,
  },
];

export const SALARY_LEDGER = [
  {
    driverId: 'DRV-001',
    driverName: 'Ahmed Al Mansoori',
    basicSalary: 4500,
    tripIncentives: 800,
    overtime: 625,
    nightAllowance: 150,
    fuelSavingBonus: 200,
    safeDrivingBonus: 300,
    penalties: 0,
  },
  {
    driverId: 'DRV-002',
    driverName: 'Rajesh Kumar',
    basicSalary: 3800,
    tripIncentives: 950,
    overtime: 760,
    nightAllowance: 0,
    fuelSavingBonus: 150,
    safeDrivingBonus: 250,
    penalties: 200,
  },
  {
    driverId: 'DRV-003',
    driverName: 'Mohammed Hassan',
    basicSalary: 4200,
    tripIncentives: 1100,
    overtime: 650,
    nightAllowance: 200,
    fuelSavingBonus: 0,
    safeDrivingBonus: 300,
    penalties: 500,
  },
  {
    driverId: 'DRV-004',
    driverName: 'Yusuf Ibrahim',
    basicSalary: 3500,
    tripIncentives: 600,
    overtime: 0,
    nightAllowance: 0,
    fuelSavingBonus: 100,
    safeDrivingBonus: 200,
    penalties: 0,
  },
  {
    driverId: 'DRV-005',
    driverName: 'Imran Ali Khan',
    basicSalary: 3600,
    tripIncentives: 720,
    overtime: 480,
    nightAllowance: 100,
    fuelSavingBonus: 120,
    safeDrivingBonus: 220,
    penalties: 0,
  },
  {
    driverId: 'DRV-006',
    driverName: 'Vijay Nair',
    basicSalary: 4100,
    tripIncentives: 880,
    overtime: 700,
    nightAllowance: 150,
    fuelSavingBonus: 180,
    safeDrivingBonus: 280,
    penalties: 150,
  },
  {
    driverId: 'DRV-007',
    driverName: 'Rafiq Ahmed',
    basicSalary: 3400,
    tripIncentives: 640,
    overtime: 420,
    nightAllowance: 0,
    fuelSavingBonus: 90,
    safeDrivingBonus: 180,
    penalties: 100,
  },
  {
    driverId: 'DRV-008',
    driverName: 'Arjun Singh',
    basicSalary: 3900,
    tripIncentives: 780,
    overtime: 550,
    nightAllowance: 120,
    fuelSavingBonus: 140,
    safeDrivingBonus: 260,
    penalties: 0,
  },
  {
    driverId: 'DRV-009',
    driverName: 'Kamran Siddiqui',
    basicSalary: 3700,
    tripIncentives: 690,
    overtime: 380,
    nightAllowance: 80,
    fuelSavingBonus: 110,
    safeDrivingBonus: 210,
    penalties: 250,
  },
  {
    driverId: 'DRV-010',
    driverName: 'Hasan Mahmud',
    basicSalary: 3300,
    tripIncentives: 580,
    overtime: 320,
    nightAllowance: 0,
    fuelSavingBonus: 80,
    safeDrivingBonus: 170,
    penalties: 0,
  },
];

export function calcNetSalary(record) {
  const earnings =
    record.basicSalary +
    record.tripIncentives +
    record.overtime +
    (record.nightAllowance || 0) +
    (record.fuelSavingBonus || 0) +
    (record.safeDrivingBonus || 0);
  return earnings - (record.penalties || 0);
}

export const PRIMARY_CERT_OPTIONS = [
  { value: 'h2sTraining', label: 'H2S Training' },
  { value: 'adrCertificate', label: 'ADR Certificate' },
  { value: 'defensiveDriving', label: 'Defensive Driving' },
  { value: 'firstAid', label: 'First Aid' },
  { value: 'fireFighting', label: 'Fire Fighting' },
  { value: 'adnocApproval', label: 'ADNOC Approval' },
  { value: 'enocApproval', label: 'ENOC Approval' },
  { value: 'portAccess', label: 'Port Access' },
];

export const UAE_PUNCH_LOCATIONS = [
  'Jebel Ali Depot',
  'Ruwais Refinery',
  'Fujairah Terminal',
  'Musaffah Industrial',
  'Hamriyah Free Zone',
  'Khalifa Port',
  'Al Ain Border Post',
  'Dubai Industrial City',
  'Habshan Abu Dhabi',
  'Sharjah Industrial Zone',
];

function certDate(daysFromNow) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().slice(0, 10);
}

export function generateDriverId(drivers) {
  const nums = drivers.map((d) => parseInt(d.id.replace('DRV-', ''), 10));
  const next = Math.max(0, ...nums) + 1;
  return `DRV-${String(next).padStart(3, '0')}`;
}

export function getActiveCertKeys(driver) {
  return MODAL_CERT_OPTIONS.filter(
    ({ key }) => driver.certifications?.[key],
  ).map(({ key }) => key);
}

export function getCertMicroBadges(driver) {
  return MODAL_CERT_OPTIONS.filter(({ key }) => driver.certifications?.[key]);
}

export function driverToModalForm(driver) {
  const licenseFromField = driver.drivingLicenseNumber?.trim();
  const licenseFromLegacy = driver.heavyVehicleLicense?.split('·')[0]?.trim();

  return {
    name: driver.name,
    nationality: driver.nationality,
    mobile: driver.mobile,
    emiratesId: driver.emiratesId,
    passport: driver.passport || '',
    visa: driver.visa || '',
    drivingLicenseNumber: licenseFromField || licenseFromLegacy || '',
    selectedCerts: getActiveCertKeys(driver),
    profilePhoto: driver.profilePhoto || null,
    identityVault: {
      ...EMPTY_IDENTITY_VAULT,
      ...(driver.identityVault || {}),
    },
    certAttachments: { ...(driver.certAttachments || {}) },
    contractDetails: normalizeContractDetails(
      driver.contractDetails || DEFAULT_CONTRACT_DETAILS,
    ),
  };
}

export function buildCertificationsFromSelection(selectedKeys, existing = {}) {
  const certifications = {};
  MODAL_CERT_OPTIONS.forEach(({ key }) => {
    if (selectedKeys.includes(key)) {
      certifications[key] = existing[key] || certDate(120 + Math.floor(Math.random() * 200));
    }
  });
  return certifications;
}

export function buildDriverFromForm(form, id, existingCerts = {}) {
  const certifications = buildCertificationsFromSelection(form.selectedCerts, existingCerts);
  const hvExpiry = certifications.heavyVehicleLicense;

  const certAttachments = {};
  form.selectedCerts.forEach((key) => {
    if (form.certAttachments?.[key]) {
      certAttachments[key] = form.certAttachments[key];
    }
  });

  return {
    id,
    name: form.name.trim(),
    nationality: form.nationality.trim(),
    mobile: form.mobile.trim(),
    emiratesId: form.emiratesId.trim(),
    passport: form.passport.trim() || 'Pending upload',
    visa: form.visa.trim() || 'Employment · Pending verification',
    drivingLicenseNumber: form.drivingLicenseNumber.trim(),
    heavyVehicleLicense: hvExpiry
      ? `${form.drivingLicenseNumber.trim() || 'HL-Pending'} · Exp ${hvExpiry}`
      : form.drivingLicenseNumber.trim() || 'HL-Pending · Exp TBC',
    certifications,
    profilePhoto: form.profilePhoto || null,
    identityVault: { ...(form.identityVault || EMPTY_IDENTITY_VAULT) },
    certAttachments,
    contractDetails: normalizeContractDetails(form.contractDetails),
  };
}

export function freshDriverModalForm() {
  return {
    ...EMPTY_DRIVER_MODAL_FORM,
    selectedCerts: [],
    identityVault: { ...EMPTY_IDENTITY_VAULT },
    certAttachments: {},
    contractDetails: {
      ...DEFAULT_CONTRACT_DETAILS,
      specialAllowances: [],
    },
  };
}

export function createDriverProfile({
  id,
  name,
  nationality,
  mobile,
  emiratesId,
  primaryCertKey,
}) {
  return buildDriverFromForm(
    {
      name,
      nationality,
      mobile,
      emiratesId,
      passport: '',
      visa: '',
      drivingLicenseNumber: '',
      selectedCerts: primaryCertKey ? [primaryCertKey] : [],
    },
    id,
  );
}

export function formatPunchTime(date = new Date()) {
  return date.toLocaleTimeString('en-AE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Dubai',
  });
}

export function formatPunchDate(date = new Date()) {
  return date.toLocaleDateString('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Dubai',
  });
}

export function randomUaeLocation() {
  return UAE_PUNCH_LOCATIONS[Math.floor(Math.random() * UAE_PUNCH_LOCATIONS.length)];
}

export function getGeofenceById(geofenceId) {
  return AUTHORIZED_GEOFENCES.find((g) => g.id === geofenceId) || null;
}

export function calcDutyHours(startIso, endIso) {
  if (!startIso || !endIso) return 0;
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (ms <= 0) return 0;
  return Math.round((ms / 3600000) * 100) / 100;
}

export function deriveTimesheetsFromPunches(feedEvents, baseTimesheets) {
  const today = formatPunchDate();

  return baseTimesheets.map((base) => {
    const events = feedEvents
      .filter((e) => e.driverId === base.driverId)
      .filter((e) => formatPunchDate(new Date(e.timestamp)) === today)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    let closedHours = 0;
    let activeDutyHours = 0;
    let deniedCount = 0;
    let openStart = null;

    events.forEach((e) => {
      if (e.geofenceStatus === 'failed') {
        deniedCount += 1;
        return;
      }
      if (e.punchType === 'PUNCH_IN') {
        openStart = e.timestamp;
      } else if (e.punchType === 'PUNCH_OUT' && openStart) {
        closedHours += calcDutyHours(openStart, e.timestamp);
        openStart = null;
      }
    });

    if (openStart) {
      activeDutyHours = calcDutyHours(openStart, new Date().toISOString());
    }

    const totalHours = Math.round((closedHours + activeDutyHours) * 100) / 100;
    const useLive = events.some((e) => e.geofenceStatus === 'success');
    const workingHours = useLive && totalHours > 0 ? totalHours : base.workingHours;
    const overtime = useLive && totalHours > 0
      ? Math.max(0, Math.round((totalHours - 8) * 100) / 100)
      : base.overtime;

    return {
      ...base,
      workingHours,
      overtime,
      activeDutyHours: Math.round(activeDutyHours * 100) / 100,
      closedDutyHours: Math.round(closedHours * 100) / 100,
      hasAnomaly: deniedCount > 0,
      deniedCount,
      lateEntryCount: 0,
      verifiedPunchCount: events.filter((e) => e.geofenceStatus === 'success').length,
    };
  });
}

export function netSalaryColor(net, basic) {
  if (net < 0) return 'text-red-700';
  if (net >= basic * 1.4) return 'text-emerald-700';
  if (net >= basic * 1.1) return 'text-logi-accent';
  return 'text-amber-700';
}
